import * as React from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type ConfirmOptions = {
  title?: string;
  description: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  /**
   * Garde-fou anti-clic accidentel : si fourni, l'utilisateur doit saisir
   * EXACTEMENT cette valeur (ex. l'email du compte) pour activer le bouton de
   * confirmation. Idéal pour les actions destructives irréversibles.
   */
  requireText?: string;
  /** Libellé affiché au-dessus du champ de saisie (défaut générique sinon). */
  requireTextLabel?: React.ReactNode;
};

type Subscriber = (opts: ConfirmOptions, resolve: (value: boolean) => void) => void;

let subscriber: Subscriber | null = null;

// `confirmDialog` is the imperative API paired with `ConfirmDialogProvider` below
// (message-channel pattern via the module-level `subscriber`); splitting it out would
// only churn the ~15 call sites for a dev-only Fast Refresh warning.
// eslint-disable-next-line react-refresh/only-export-components
export function confirmDialog(options: ConfirmOptions): Promise<boolean> {
  if (!subscriber) {
    if (typeof window !== "undefined") {
      return Promise.resolve(window.confirm(stringifyDescription(options.description)));
    }
    return Promise.resolve(false);
  }
  return new Promise<boolean>((resolve) => {
    subscriber!(options, resolve);
  });
}

function stringifyDescription(node: React.ReactNode): string {
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  return "";
}

type State = {
  open: boolean;
  options: ConfirmOptions | null;
  resolve: ((value: boolean) => void) | null;
};

const INITIAL_STATE: State = { open: false, options: null, resolve: null };

export function ConfirmDialogProvider() {
  const [state, setState] = React.useState<State>(INITIAL_STATE);
  const [confirmInput, setConfirmInput] = React.useState("");
  const pendingResolveRef = React.useRef<{
    resolve: (value: boolean) => void;
    value: boolean;
  } | null>(null);

  React.useEffect(() => {
    subscriber = (options, resolve) => {
      if (pendingResolveRef.current) {
        pendingResolveRef.current.resolve(false);
        pendingResolveRef.current = null;
      }
      setConfirmInput("");
      setState({ open: true, options, resolve });
    };
    return () => {
      subscriber = null;
    };
  }, []);

  const handleResolve = React.useCallback(
    (value: boolean) => {
      const resolve = state.resolve;
      if (resolve && !pendingResolveRef.current) {
        pendingResolveRef.current = { resolve, value };
      }
      setState((prev) => ({ ...prev, open: false }));
    },
    [state.resolve],
  );

  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      if (!open) handleResolve(false);
    },
    [handleResolve],
  );

  // Radix appelle onCloseAutoFocus après son cycle d'animation + sa tentative
  // de restoreFocus. On y draine la promise, ce qui garantit que les mutations
  // du caller (qui démontent le trigger) interviennent après le nettoyage de
  // body.style.pointerEvents par Radix.
  const handleCloseAutoFocus = React.useCallback((e: Event) => {
    e.preventDefault();
    const pending = pendingResolveRef.current;
    if (!pending) return;
    pendingResolveRef.current = null;
    queueMicrotask(() => {
      setState(INITIAL_STATE);
      pending.resolve(pending.value);
    });
  }, []);

  const opts = state.options;
  const requireText = opts?.requireText;
  const canConfirm = !requireText || confirmInput.trim() === requireText;

  return (
    <AlertDialog open={state.open} onOpenChange={handleOpenChange}>
      <AlertDialogContent onCloseAutoFocus={handleCloseAutoFocus}>
        <AlertDialogHeader>
          <AlertDialogTitle>{opts?.title ?? "Confirmer l'action"}</AlertDialogTitle>
          {opts?.description ? (
            <AlertDialogDescription>{opts.description}</AlertDialogDescription>
          ) : null}
        </AlertDialogHeader>
        {requireText ? (
          <div className="space-y-1.5">
            <label className="text-sm text-muted-foreground">
              {opts?.requireTextLabel ?? (
                <>
                  Pour confirmer, saisissez{" "}
                  <code className="rounded bg-muted px-1 py-0.5 text-xs">{requireText}</code>
                </>
              )}
            </label>
            <input
              autoFocus
              value={confirmInput}
              onChange={(e) => setConfirmInput(e.target.value)}
              placeholder={requireText}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            />
          </div>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => handleResolve(false)}>
            {opts?.cancelLabel ?? "Annuler"}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={!canConfirm}
            className={cn(
              opts?.destructive &&
                buttonVariants({ variant: "destructive" }),
              !canConfirm && "pointer-events-none opacity-50",
            )}
            onClick={() => handleResolve(true)}
          >
            {opts?.confirmLabel ?? "Confirmer"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
